import { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { Form, Row, Col, Button, Card, Image } from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import icValidationError from './assets/img/ic-danger.svg';
import occupations from './data/occupations';
import icDelete from './assets/img/ic-delete-danger.svg';
import icLaptop from './assets/img/ic-laptop-add.svg';
const EMPTY_OCC = { major: '', subMajor: '', minor: '', unit: '', skill: '' };

const UserAddOccupation = forwardRef(({ onOccupationsChange, handleReset, formData, errors, isEditPage, handleOccupationSubmit, hideEdit }, ref) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({});


    const [occupationsList, setOccupationsList] = useState(() => {
        const rows = Array.isArray(formData?.occupations) ? formData.occupations : [];
        return rows.length
            ? rows.map(row => ({ ...EMPTY_OCC, ...row })).slice(0, 3)
            : [{ ...EMPTY_OCC }];
    });

    // Parent -> Child: add flows also need persisted values as editable initial values.
    useEffect(() => {
        if (isEditPage && isEditing) return; // profile edit flow owns its local draft
        const rows = Array.isArray(formData?.occupations) ? formData.occupations : [];
        const nextRows = rows.length
            ? rows.map(r => ({ ...EMPTY_OCC, ...r })).slice(0, 3)
            : [{ ...EMPTY_OCC }];

        setOccupationsList(currentRows => (
            JSON.stringify(currentRows) === JSON.stringify(nextRows) ? currentRows : nextRows
        ));
    }, [formData?.occupations, isEditing, isEditPage]);

    // Child -> Parent: live sync only for ADD flow (isEditPage = false)
    useEffect(() => {
        if (!onOccupationsChange) return;
        if (isEditPage) return;     // edit flow: don't live sync
        onOccupationsChange(occupationsList);
    }, [occupationsList, onOccupationsChange, isEditPage]);

    useImperativeHandle(ref, () => ({
        validate: () => {
            let isValid = true;

            const errorList = occupationsList.map((occ) => {
                const errors = {};

                if (!occ.major) {
                    errors.major = 'Major group is required.';
                    isValid = false;
                }
                if (!occ.subMajor) {
                    errors.subMajor = 'Sub-major group is required.';
                    isValid = false;
                }
                if (!occ.minor) {
                    errors.minor = 'Minor group is required.';
                    isValid = false;
                }
                if (!occ.skill) {
                    errors.skill = 'Skill level is required.';
                    isValid = false;
                }

                return errors;
            });

            setLocalErrors(errorList);
            return isValid;
        }
    }));


    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleOccupationSubmit === 'function') { // ✅ Ensure it's a function before calling
            onOccupationsChange && onOccupationsChange(occupationsList); // ✅ only on Save
            const ok = await handleOccupationSubmit(e, occupationsList); // ✅ pass the source of truth
            if (ok) setIsEditing(false)
        } else {
            console.error('Error: handleOccupationSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        if (typeof handleReset === 'function') {
            handleReset(tempFormData); // ✅ Restore saved values + clear errors
        }
    };

    const onAddOccupation = () => {
        setOccupationsList(prev => (prev.length >= 3 ? prev : [...prev, { ...EMPTY_OCC }]))
    };


    const onRemoveOccupation = (indexToRemove) => {
        setOccupationsList(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleOccupationChange = (index, field, value) => {
        const updated = [...occupationsList];
        updated[index][field] = value;

   // Clear dependent levels
    if (field === 'major') {
        updated[index].subMajor = '';
        updated[index].minor = '';
        updated[index].unit = '';
        updated[index].skill = ''; // ✅ reset skill
    } else if (field === 'subMajor') {
        updated[index].minor = '';
        updated[index].unit = '';
        updated[index].skill = ''; // ✅ reset skill
    } else if (field === 'minor') {
        updated[index].unit = '';
        updated[index].skill = ''; // ✅ reset skill
    }

    setOccupationsList(updated);
    };


    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row>
                        <Col>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">3.</span> Occupation</h3>
                            <h3 className="d-none d-lg-flex"><span className="item-number">3.</span> Occupation</h3>
                            </div>
                            <hr />
                        </Col>
                    </Row>
                    <>
                        {isEditing ? (
                            <>
                                <Row>
                                    {/* Form Fields */}
                                    <Col md={12}>
                                        <Row>
                                            {occupationsList.map((occ, index) => {
                                                const major = occupations.find(g => g.code === occ.major);
                                                const subMajor = major?.children.find(s => s.code === occ.subMajor);
                                                const minor = subMajor?.children.find(m => m.code === occ.minor);

                                                return (
                                                    <Col md={4} key={index}>
                                                        <Card className="mb-4">
                                                            <Card.Body className="pb-3 profile-edit-field-stack">
                                                                <Form.Group className="validated mb-3" controlId={`majorGroup-${index}`}>
                                                                    <Form.Label>Major Groups <span>*</span></Form.Label>
                                                                    <Form.Select
                                                                        value={occ.major}
                                                                        onChange={e => handleOccupationChange(index, 'major', e.target.value)}
                                                                        className="form-select"
                                                                        isInvalid={!!errors.occupationsList?.[index]?.major || !!localErrors[index]?.major} // ✅ Add isInvalid
                                                                    >
                                                                        <option value="">Select a major group</option>
                                                                        {occupations.map(group => (
                                                                            <option key={group.code} value={group.code}>
                                                                                {group.title}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>

                                                                    {(errors.occupationsList?.[index]?.major || localErrors[index]?.major) && (
                                                                        <div className="custom-error-icon-wrapper">
                                                                            <img
                                                                                src={icValidationError}
                                                                                alt={errors.occupationsList?.[index]?.major || localErrors[index]?.major}
                                                                                className="custom-error-icon"
                                                                            />
                                                                            <div className="custom-tooltip">
                                                                                {errors.occupationsList?.[index]?.major || localErrors[index]?.major}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Form.Group>


                                                                <Form.Group className="validated mb-3" controlId={`subMajorGroup-${index}`}>
                                                                    <Form.Label>Sub-Major Groups <span>*</span></Form.Label>
                                                                    <Form.Select
                                                                        value={occ.subMajor}
                                                                        onChange={e => handleOccupationChange(index, 'subMajor', e.target.value)}
                                                                        disabled={!occ.major}
                                                                        className="form-select"
                                                                        isInvalid={!!errors.occupationsList?.[index]?.subMajor || !!localErrors[index]?.subMajor} // ✅ Add isInvalid
                                                                    >
                                                                        <option value="">Select a sub-major group</option>
                                                                        {major?.children.map(sub => (
                                                                            <option key={sub.code} value={sub.code}>
                                                                                {sub.title}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>

                                                                    {(errors.occupationsList?.[index]?.subMajor || localErrors[index]?.subMajor) && (
                                                                        <div className="custom-error-icon-wrapper">
                                                                            <img
                                                                                src={icValidationError}
                                                                                alt={errors.occupationsList?.[index]?.subMajor || localErrors[index]?.subMajor}
                                                                                className="custom-error-icon"
                                                                            />
                                                                            <div className="custom-tooltip">
                                                                                {errors.occupationsList?.[index]?.subMajor || localErrors[index]?.subMajor}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Form.Group>


                                                                <Form.Group className="validated mb-3" controlId={`minorGroup-${index}`}>
                                                                    <Form.Label>Minor Groups <span>*</span></Form.Label>
                                                                    <Form.Select
                                                                        value={occ.minor}
                                                                        onChange={e => handleOccupationChange(index, 'minor', e.target.value)}
                                                                        disabled={!occ.subMajor}
                                                                        className="form-select"
                                                                        isInvalid={!!errors.occupationsList?.[index]?.minor || !!localErrors[index]?.minor} // ✅ Add isInvalid
                                                                    >
                                                                        <option value="">Select a minor group</option>
                                                                        {subMajor?.children.map(minor => (
                                                                            <option key={minor.code} value={minor.code}>
                                                                                {minor.title}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>

                                                                    {(errors.occupationsList?.[index]?.minor || localErrors[index]?.minor) && (
                                                                        <div className="custom-error-icon-wrapper">
                                                                            <img
                                                                                src={icValidationError}
                                                                                alt={errors.occupationsList?.[index]?.minor || localErrors[index]?.minor}
                                                                                className="custom-error-icon"
                                                                            />
                                                                            <div className="custom-tooltip">
                                                                                {errors.occupationsList?.[index]?.minor || localErrors[index]?.minor}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Form.Group>


                                                                <Form.Group className="mb-3" controlId={`unitGroup-${index}`}>
                                                                    <Form.Label>Unit Groups</Form.Label>
                                                                    <Form.Select
                                                                        value={occ.unit}
                                                                        onChange={e => handleOccupationChange(index, 'unit', e.target.value)}
                                                                        disabled={!occ.minor}
                                                                    >
                                                                        <option value="">Select a unit group</option>
                                                                        {minor?.children.map(unit => (
                                                                            <option key={unit.code} value={unit.code}>
                                                                            {unit.title}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </Form.Group>

                                                                <Form.Group className="validated mb-3" controlId={`skillLevel-${index}`}>
                                                                    <Form.Label>Skill Level <span>*</span></Form.Label>
                                                                    <Form.Select
                                                                        value={occ.skill}
                                                                        onChange={e => handleOccupationChange(index, 'skill', e.target.value)}
                                                                        className="form-select"
                                                                        isInvalid={!!errors.occupationsList?.[index]?.skill || !!localErrors[index]?.skill} // ✅ Add isInvalid
                                                                    >
                                                                        <option value="">Select skill level</option>
                                                                        <option value="1">Level 1 - Elementary (ISCED 0–1)</option>
                                                                        <option value="2">Level 2 - Clerical, etc. (ISCED 2)</option>
                                                                        <option value="3">Level 3 - Technicians (ISCED 3–4)</option>
                                                                        <option value="4">Level 4 - Professionals (ISCED 5–6)</option>
                                                                    </Form.Select>

                                                                    {(errors.occupationsList?.[index]?.skill || localErrors[index]?.skill) && (
                                                                        <div className="custom-error-icon-wrapper">
                                                                            <img
                                                                                src={icValidationError}
                                                                                alt={errors.occupationsList?.[index]?.skill || localErrors[index]?.skill}
                                                                                className="custom-error-icon"
                                                                            />
                                                                            <div className="custom-tooltip">
                                                                                {errors.occupationsList?.[index]?.skill || localErrors[index]?.skill}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Form.Group>

                                                                <Row className="mt-3">
                                                                    <Col className="text-end">
                                                                        {index > 0 ? (
                                                                            <Button
                                                                                variant="link"
                                                                                size="sm"
                                                                                onClick={() => onRemoveOccupation(index)}
                                                                                className="align-center text-danger p-0"
                                                                            >
                                                                                <Image src={icDelete} alt="Remove" className="me-1" />
                                                                                Remove occupation
                                                                            </Button>
                                                                        ) : (
                                                                            <div style={{ height: '32px' }}></div>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                        {isEditing && (
                                            <Row>
                                                <Col md={12} className="justify-content-end d-flex">
                                                    <Button
                                                        variant="primary"
                                                        onClick={onAddOccupation}
                                                        className="btn-sm mb-4"
                                                        disabled={occupationsList.length >= 3}
                                                    >
                                                        Add Occupation
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <>
                               <Row>
                                    {(occupationsList ?? [])
                                        .filter(o => o && (o.major || o.subMajor || o.minor || o.unit || o.skill))
                                        .map((o, idx) => {
                                        const majorNode = occupations.find(g => g.code === o.major);
                                        const subNode   = majorNode?.children?.find(s => s.code === o.subMajor);
                                        const minorNode = subNode?.children?.find(m => m.code === o.minor);
                                        const unitNode  = minorNode?.children?.find(u => u.code === o.unit);

                                        const majorTitle = majorNode?.title ?? 'N/A';
                                        const subTitle   = subNode?.title   ?? 'N/A';
                                        const minorTitle = minorNode?.title ?? 'N/A';
                                        const unitTitle  = unitNode?.title  ?? 'N/A';
                                        const skillNumber = o.skill ? String(o.skill) : 'N/A';
                                        

                                        return (
                                            <Col md={4} key={`${o.major || 'empty'}-${idx}`}>
                                                <Card>
                                                    <Card.Body className="p-3">
                                                        <div className="icon-text">
                                                            <span className="icon" aria-hidden="true">
                                                                <Image src={icLaptop} alt="Major group" />
                                                            </span>
                                                            <span className="text">
                                                                Major group
                                                                <br />
                                                                <strong>{majorTitle}</strong>
                                                            </span>
                                                        </div>
                                                    </Card.Body>
                                                </Card>

                                                <Row className="sub-card-row ps-3 pe-2">
                                                    <Col md={12}>
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        1
                                                                    </span>
                                                                    <span className="text">
                                                                        Sub-Major groups:
                                                                        <br />
                                                                        <strong>{subTitle}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={12} className="mt-3 p-0">
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        2
                                                                    </span>
                                                                    <span className="text">
                                                                        Minor Groups:
                                                                        <br />
                                                                        <strong>{minorTitle}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={12} className="mt-3 p-0">
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        3
                                                                    </span>
                                                                    <span className="text">
                                                                        Unit Groups:
                                                                        <br />
                                                                        <strong>{unitTitle}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={12} className="mt-3 p-0">
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        4
                                                                    </span>
                                                                    <span className="text">
                                                                        Skill level:
                                                                        <br />
                                                                        <strong>{skillNumber}</strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        );
                                    })}
                                </Row>

                            </>
                        )}
                    </>
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
});

export default UserAddOccupation
